import { Pencil } from 'lucide-react';
import { useEffect, useRef, useState, type FC } from 'react';
import { cn } from '../lib/utils';

interface InlineRenameProps {
  value: string;
  onChange: (value: string) => void;
  /** Names the input while editing, e.g. "Dataset name". */
  label: string;
  /** Control editing from outside, e.g. to bind a rename key. */
  editing?: boolean;
  onEditingChange?: (editing: boolean) => void;
  /** The element the title renders as when not editing. */
  as?: 'h1' | 'h2' | 'span';
  className?: string;
}

/**
 * A title you click to rename (DESIGN.md §8.9). ↵ or blur saves, Esc
 * cancels, and an empty name is never saved.
 */
export const InlineRename: FC<InlineRenameProps> = ({
  value,
  onChange,
  label,
  editing: controlled,
  onEditingChange,
  as: Heading = 'h1',
  className
}) => {
  const [uncontrolled, setUncontrolled] = useState(false);
  const editing = controlled ?? uncontrolled;
  const setEditing = (next: boolean) => {
    setUncontrolled(next);
    onEditingChange?.(next);
  };
  // Uncontrolled: the input mounts fresh, seeded from `value`, each time editing starts.
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) input.current?.select();
  }, [editing]);

  const commit = () => {
    const next = (input.current?.value ?? '').trim();
    if (next && next !== value) onChange(next);
    setEditing(false);
  };

  return editing ? (
    <input
      ref={input}
      aria-label={label}
      defaultValue={value}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === 'Enter') commit();
        if (event.key === 'Escape') {
          // Esc here cancels the rename, not whatever layer sits behind it.
          event.stopPropagation();
          // Reset first, so a blur fired as the input unmounts saves nothing.
          event.currentTarget.value = value;
          setEditing(false);
        }
      }}
      className={cn(
        'h-9 w-[360px] max-w-full rounded-md border border-brand-border bg-transparent px-2 text-title-sm text-foreground ring-4 ring-brand-subtle outline-none',
        className
      )}
    />
  ) : (
    <Heading className={cn('text-title-sm text-foreground', className)}>
      <button
        type="button"
        onClick={() => setEditing(true)}
        aria-label={`${value}, rename`}
        className="group -mx-2 flex h-9 cursor-text items-center gap-2 rounded-md border border-transparent px-2 outline-none hover:border-border-strong hover:bg-surface-hover focus-visible:ring-2 focus-visible:ring-ring"
      >
        {value}
        <Pencil
          aria-hidden="true"
          className="size-3.5 text-muted-foreground group-hover:text-foreground"
        />
      </button>
    </Heading>
  );
};
